import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, VerifyData, SyncData, IncompleteData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { Refresh, Search } from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box,
  TextField, InputAdornment, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper
} from '@mui/material';
import StockTransferInModal from './StockTransferInModal';
import Pagination from '../../common/Pagination';
import './StockTransferInIndex.css';

const StockTransferInIndex = () => {
  useSessionValidation();
  const { companyId, branchId, token, userId, permissions } = useAuth();
  const { logAudit } = useAuditLog();

  const hasCreateMasterPermission = permissions?.includes('Create Master');
  const hasEditMasterPermission = permissions?.includes('Edit Master');
  const hasDeleteMasterPermission = permissions?.includes('Delete Master');

  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const modalActionRef = useRef('');
  const [selectedItem, setSelectedItem] = useState(null); // eslint-disable-line no-unused-vars
  const [stockTransfers, setStockTransfers] = useState([]);
  const [items, setItems] = useState([]);
  const [branches, setBranches] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');

  const formatDateToYYYYMMDD = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };

  const getCurrentDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getCurrentDate());
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({});
  const [transferDetails, setTransferDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');
  const [verifyModalOpen, setVerifyModalOpen] = useState(false);
  const [verifyItem, setVerifyItem] = useState(null);

  useEffect(() => {
    if (companyId && token && fromDate && toDate) fetchAllData();
  }, [companyId, token, fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStockTransfers = async () => {
    try {
      const activeRes = await fetchData('StockInAndOutApi', `/StockTranferInList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeRes.response_data || activeRes || []).map(i => ({ ...i, IsActive: i.IsActive ?? true }));
      setStockTransfers([...activeData]);
    } catch (e) { console.error(e); setStockTransfers([]); }
  };

  const fetchItems = async () => {
    try {
      const res = await fetchData('ItemApi', `/${companyId}`, token);
      setItems(res.response_data || res || []);
    } catch (e) { setItems([]); }
  };

  const fetchBranches = async () => {
    try {
      const res = await fetchData('BranchApi', `/${companyId}`, token);
      setBranches(res.response_data || res || []);
    } catch (e) { setBranches([]); }
  };

  const fetchMaxTransferNo = async () => {
    try {
      const res = await fetchData('StockInAndOutApi', `/MaxTransferNo/${companyId}/${branchId || 1}`, token);
      const data = res.response_data || res || {};
      return {
        transferNumber: data.TransferNumber || data.transferNumber || 1,
        transferPrefix: data.TransferPrefix || data.transferPrefix || 'STI',
        finYear: data.FinYear || data.finYear || ''
      };
    } catch (e) { return { transferNumber: 1, transferPrefix: 'STI', finYear: '' }; }
  };

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setLoadingMessage('Loading stock transfers...');
    try {
      await Promise.allSettled([fetchStockTransfers(), fetchItems(), fetchBranches()]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, branchId, token, fromDate, toDate]);

  const fetchSingleStockTransfer = async (id) => {
    try {
      const res = await fetchData('StockInAndOutApi', `/${id}`, token);
      const data = res.response_data || res;
      console.log('StockTransferIn single fetch keys:', Object.keys(data));
      return data;
    } catch (e) { return null; }
  };

  const handleOpenModal = async (action, itemOrId = null) => {
    modalActionRef.current = action;
    setModalAction(action);
    if (action === 'view') {
      const recordId = typeof itemOrId === 'object' ? (itemOrId?.Id || 0) : (itemOrId || 0);
      logAudit(AUDIT_ACTION.VIEW, 'StockTransferInApi', recordId);
    }
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;
    if ((action === 'view' || action === 'edit') && itemId) {
      setModalLoading(true);
      const freshData = await fetchSingleStockTransfer(itemId);
      if (freshData) {
        const t = freshData.StockTransfer || freshData;
        const details = freshData.StockTransferDetails || [];
        setSelectedItem(t);
        setFormData({
          Id: t.Id, CompanyId: t.CompanyId || companyId, BranchId: t.ToBranchId || branchId,
          FromBranchId: t.FromBranchId,
          TransferNumber: t.StockTransferNumber,
          TransferPrefix: t.StockTransferPrefix || '',
          FinYear: t.FinYear || '',
          TransferDate: t.StockTransferDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          TotalQuantity: t.TotalQuantity || 0,
          TotalAmount: t.GrandTotal || 0,
          Remarks: t.Remarks || '',
          IsVerified: t.InputAccepted || false,
          IsActive: t.IsActive ?? true,
          CreatedBy: t.CreatedBy, CreatedDate: t.CreatedDate,
          CreatedByName: t.FromBranchName,
          ModifiedBy: t.UpdatedBy, ModifiedDate: t.UpdatedDate,
          InputAccepted: t.InputAccepted ?? null,
          InputRejected: t.InputRejected ?? null,
          TransporterName: t.TransporterName || '',
          TransporterGST: t.TransporterGST || '',
          VehicleNumber: t.VehicleNumber || '',
          Distance: t.Distance || '',
          TaxableAmount: t.TaxableAmount || 0,
          CGSTAmount: t.CGSTAmount || 0,
          SGSTAmount: t.SGSTAmount || 0,
          IGSTAmount: t.IGSTAmount || 0,
          RoundOff: t.RoundOff || 0,
          GrandTotal: t.GrandTotal || 0,
        });
        setTransferDetails(details.map(d => ({
          Id: d.Id || 0,
          ItemId: d.ItemId,
          Quantity: d.Quantity || 0,
          Rate: d.Rate || 0,
          Amount: d.Amount || 0,
          HSNCode: d.HSNCode || '',
          GSTRate: d.GSTRate || 0,
          TaxableAmount: d.TaxableAmount || 0,
          CGSTAmount: d.CGSTAmount || 0,
          SGSTAmount: d.SGSTAmount || 0,
          IGSTAmount: d.IGSTAmount || 0,
          TotalAmount: d.TotalAmount || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: d.CreatedBy, CreatedDate: d.CreatedDate,
          ModifiedBy: d.UpdatedBy, ModifiedDate: d.UpdatedDate
        })));
      }
      setModalLoading(false);
    } else {
      setModalLoading(true);
      const maxData = await fetchMaxTransferNo();
      setSelectedItem(null);
      setFormData({
        CompanyId: companyId, BranchId: branchId || 1, FromBranchId: '',
        TransferNumber: maxData.transferNumber, TransferPrefix: maxData.transferPrefix,
        FinYear: maxData.finYear, TransferDate: new Date().toISOString().split('T')[0],
        TotalQuantity: 0, TotalAmount: 0, Remarks: '', IsVerified: false, IsActive: true
      });
      setTransferDetails([]);
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false); setModalAction(''); setSelectedItem(null);
    setFormData({}); setTransferDetails([]);
  };

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      setLoadingMessage(`${modalAction === 'create' ? 'Creating' : 'Updating'} stock transfer...`);
      const dataToSend = {
        StockTransferIn: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId, BranchId: formData.BranchId || branchId,
          FromBranchId: parseInt(formData.FromBranchId) || 0,
          TransferNumber: formData.TransferNumber || '', TransferPrefix: formData.TransferPrefix || '',
          FinYear: formData.FinYear || '',
          TransferDate: formData.TransferDate || new Date().toISOString().split('T')[0],
          TotalQuantity: parseFloat(formData.TotalQuantity) || 0,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          Remarks: formData.Remarks || '', IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : formData.CreatedDate,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        StockTransferInDetails: transferDetails.map(d => ({
          Id: modalAction === 'edit' ? (d.Id || 0) : 0, CompanyId: companyId,
          StockTransferInId: modalAction === 'edit' ? formData.Id : 0,
          ItemId: parseInt(d.ItemId) || 0, Quantity: parseFloat(d.Quantity) || 0,
          Rate: parseFloat(d.Rate) || 0, Amount: parseFloat(d.Amount) || 0,
          IsActive: d.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : d.CreatedBy,
          CreatedDate: modalAction === 'create' ? null : d.CreatedDate,
          ModifiedBy: modalAction === 'edit' ? userId : null
        }))
      };
      if (modalAction === 'create') {
        await postData('StockTransferInApi', '', dataToSend, token);
        setSuccess('Stock Transfer In created successfully!');
        logAudit(AUDIT_ACTION.CREATE, 'StockTransferInApi', 0, null, { BillNumber: formData.BillNumber || '' });
      } else {
        if (!formData.Id) throw new Error('ID missing');
        await updateData('StockTransferInApi', formData.Id, dataToSend, token);
        setSuccess('Stock Transfer In updated successfully!');
        logAudit(AUDIT_ACTION.UPDATE, 'StockTransferInApi', formData.Id, { ...selectedItem }, { ...formData });
      }
      handleCloseModal();
      await fetchStockTransfers();
    } catch (e) {
      console.error(e);
      setError(`Failed to ${modalAction} stock transfer. Please try again.`);
    }
    setLoading(false);
  };

  const handleOpenDeleteModal = (id, no) => { setDeleteItemId(id); setDeleteItemNo(no); setDeleteModalOpen(true); };
  const handleCloseDeleteModal = () => { setDeleteModalOpen(false); setDeleteItemId(null); setDeleteItemNo(''); };

  const handleDelete = async () => {
    if (!deleteItemId) return;
    setLoading(true); handleCloseDeleteModal();
    try {
      await deleteData('StockTransferInApi', deleteItemId, token);
      setSuccess('Deleted successfully!');
      await fetchStockTransfers();
      logAudit(AUDIT_ACTION.DELETE, 'StockTransferInApi', deleteItemId, { Id: deleteItemId }, null);
    } catch (e) { setError('Failed to delete.'); }
    setLoading(false);
  };

  const handleOpenActivateModal = (id, no) => { setActivateItemId(id); setActivateItemNo(no); setActivateModalOpen(true); };
  const handleCloseActivateModal = () => { setActivateModalOpen(false); setActivateItemId(null); setActivateItemNo(''); };

  const handleActivate = async () => {
    if (!activateItemId) return;
    setLoading(true); handleCloseActivateModal();
    try { await activateData('StockTransferInApi', activateItemId, token); setSuccess('Activated successfully!'); await fetchStockTransfers(); }
    catch (e) { setError('Failed to activate.'); }
    setLoading(false);
  };

  const handleOpenVerifyModal = (item) => { setVerifyItem(item); setVerifyModalOpen(true); };
  const handleCloseVerifyModal = () => { setVerifyModalOpen(false); setVerifyItem(null); };

  const handleVerify = async () => {
    if (!verifyItem) return;
    setLoading(true); setLoadingMessage('Verifying stock transfer...'); handleCloseVerifyModal();
    try {
      await VerifyData('StockTransferInApi', verifyItem.Id, token);
      setSuccess('Stock Transfer In verified successfully!');
      logAudit(AUDIT_ACTION.VERIFY, 'StockTransferInApi', verifyItem.Id);
      await fetchStockTransfers();
    } catch (e) { setError('Failed to verify stock transfer.'); }
    setLoading(false);
  };

  const handleAccept = async (id) => {
    if (!id) return;
    setLoading(true); setLoadingMessage('Accepting stock transfer...');
    try {
      await SyncData('StockInAndOutApi', id, token);
      setSuccess('Stock transfer accepted successfully!');
      handleCloseModal();
      await fetchStockTransfers();
    } catch (e) { console.error(e); setError('Failed to accept stock transfer.'); }
    setLoading(false);
  };

  const handleReject = async (id) => {
    if (!id) return;
    setLoading(true); setLoadingMessage('Rejecting stock transfer...');
    try {
      await IncompleteData('StockInAndOutApi', id, token);
      setSuccess('Stock transfer rejected successfully!');
      handleCloseModal();
      await fetchStockTransfers();
    } catch (e) { console.error(e); setError('Failed to reject stock transfer.'); }
    setLoading(false);
  };

  const getFilteredData = (data) => {
    if (!Array.isArray(data)) return [];
    let filtered = [...data];
    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.TransferNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        branches.find(b => b.Id === item.FromBranchId)?.Name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (filterStatus !== 'all') filtered = filtered.filter(i => filterStatus === 'active' ? i.IsActive : !i.IsActive);
    if (filterVerified !== 'all') filtered = filtered.filter(i => filterVerified === 'verified' ? i.IsVerified : !i.IsVerified);
    return filtered;
  };

  const getPaginatedData = (data) => {
    const filtered = getFilteredData(data);
    const startIndex = (currentPage - 1) * rowsPerPage;
    return {
      data: filtered.slice(startIndex, startIndex + rowsPerPage),
      totalCount: filtered.length,
      totalPages: Math.ceil(filtered.length / rowsPerPage),
      startIndex
    };
  };

  const handlePageChange = (page) => setCurrentPage(page);
  const handleRowsPerPageChange = (e) => { setRowsPerPage(Number(e.target.value)); setCurrentPage(1); };
  const handleRefresh = () => fetchAllData();
  const handleCloseSnackbar = () => { setError(''); setSuccess(''); };

  const formatDate = (ds) => {
    if (!ds) return '-';
    return new Date(ds).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatCurrency = (amount) =>
    `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const { data: paginatedData, totalCount, totalPages, startIndex } = getPaginatedData(stockTransfers);

  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <LogoLoader open={loading || modalLoading} message={loadingMessage} />

      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Stock Transfer In Management</h4>
          {/* {(() => {
            const pendingCount = stockTransfers.filter(i => i.IsActive && !i.IsVerified).length;
            return pendingCount > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', px: 1.5, py: 0.5 }}>
                <span style={{ fontSize: '14px' }}>⏳</span>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#c2410c', fontSize: '12px' }}>
                  {pendingCount} Verification Pending
                </Typography>
              </Box>
            ) : null;
          })()} */}
        </div>

        {/* Search, Filters and Create */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search by Transfer No, Branch..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#0369a1', fontSize: '20px' }} /></InputAdornment> }}
            sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
          />
          <input type="date" lang="en-GB" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
          <input type="date" lang="en-GB" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
          <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
            <option value="all">All</option>
            <option value="verified">Verified</option>
            <option value="pending">Pending</option>
          </select>
          <Tooltip title="Refresh" arrow>
            <IconButton onClick={handleRefresh} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '29px', width: '29px' }}>
              <Refresh sx={{ fontSize: '16px' }} />
            </IconButton>
          </Tooltip>

        </div>

        {/* Table */}
        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #0369a1 0%, #0369a1 100%)', height: '27px' }}>
                  {['Sl No.', 'Transfer No.', 'Date', 'From Branch', 'Total Qty', 'Total Amount', 'Transfer Status', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 4 || i === 5 || i === 6 || i === 7 ? 'right' : i === 10 ? 'right' : 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {Array.isArray(paginatedData) && paginatedData.length > 0 ? (
                  paginatedData.map((item, index) => (
                    <TableRow key={item.Id || index} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>{startIndex + index + 1}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>
                        {item.StockTransferPrefix && item.FinYear
                          ? `${item.StockTransferPrefix}/${item.StockTransferNumber}/${item.FinYear}`
                          : item.TransferNumber || '-'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.StockTransferDate)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{branches.find(b => b.Id === item.FromBranchId)?.Name || '-'}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 700, textAlign: 'right' }}>{item.TotalQuantity || 0}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#059669', fontWeight: 700, textAlign: 'right' }}>{formatCurrency(item.GrandTotal)}</TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        {item.InputAccepted === true ? (
                          <Chip label="Accepted Transfer" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#bff5d2ff', color: '#15803d', fontWeight: 700 }} />
                        ) : item.InputRejected === true ? (
                          <Chip label="ST Rejected" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626', fontWeight: 700 }} />
                        ) : (
                          <Chip label="Pending" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fef9c3', color: '#a16207', fontWeight: 700 }} />
                        )}
                      </TableCell>
                      {/* <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        <Chip label={item.IsVerified ? 'Verified' : 'Pending'} color={item.IsVerified ? 'success' : 'warning'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell> */}
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                        <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <Tooltip title="View" arrow>
                            <span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handleOpenModal('view', item.Id)}>👁️</span>
                          </Tooltip>
                          {/* {item.IsActive && !item.InputAccepted && !item.InputRejected && (
                            <Tooltip title="Accept & Verify" arrow>
                              <span style={{ cursor: hasEditMasterPermission ? 'pointer' : 'not-allowed', fontSize: '14px', opacity: hasEditMasterPermission ? 1 : 0.4 }}
                                onClick={() => hasEditMasterPermission && handleOpenVerifyModal(item)}>✔️</span>
                            </Tooltip>
                          )} */}
                          {item.IsActive && !item.InputAccepted && !item.InputRejected && (
                            <Tooltip title="Edit" arrow>
                              <span style={{ cursor: hasEditMasterPermission ? 'pointer' : 'not-allowed', fontSize: '14px', opacity: hasEditMasterPermission ? 1 : 0.4 }}
                                onClick={() => hasEditMasterPermission && handleOpenModal('edit', item.Id)}>✏️</span>
                            </Tooltip>
                          )}
                          {item.IsActive && !item.InputAccepted && !item.InputRejected && (
                            <Tooltip title="Delete" arrow>
                              <span style={{ cursor: hasDeleteMasterPermission ? 'pointer' : 'not-allowed', fontSize: '14px', opacity: hasDeleteMasterPermission ? 1 : 0.4 }}
                                onClick={() => hasDeleteMasterPermission && handleOpenDeleteModal(item.Id, item.TransferNumber)}>🗑️</span>
                            </Tooltip>
                          )}
                          {!item.IsActive && (
                            <Tooltip title="Activate" arrow>
                              <span style={{ cursor: hasEditMasterPermission ? 'pointer' : 'not-allowed', fontSize: '14px', opacity: hasEditMasterPermission ? 1 : 0.4 }}
                                onClick={() => hasEditMasterPermission && handleOpenActivateModal(item.Id, item.TransferNumber)}>✅</span>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={11} style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>
                      No stock transfers found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {/* Pagination */}
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalCount={totalCount}
          rowsPerPage={rowsPerPage}
          startIndex={startIndex}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </div>

      {/* Stock Transfer In Modal */}
      {openModal && (
        <StockTransferInModal
          open={openModal} onClose={handleCloseModal} modalAction={modalActionRef.current}
          formData={formData} setFormData={setFormData}
          transferDetails={transferDetails} setTransferDetails={setTransferDetails}
          onSave={handleSave} items={items} branches={branches}
          loading={modalLoading} companyId={companyId} branchId={branchId} token={token}
          onAccept={handleAccept} onReject={handleReject}
        />
      )}

      {/* Delete Confirmation */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>🗑️</Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Delete Stock Transfer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete transfer <strong>{deleteItemNo}</strong>?
          </Typography>
          <Typography sx={{ color: '#dc2626', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ borderColor: '#d1d5db', color: '#6b7280' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation */}
      <Dialog open={activateModalOpen} onClose={handleCloseActivateModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#f0fdf4', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>✅</Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Activate Stock Transfer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to activate transfer <strong>{activateItemNo}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseActivateModal} variant="outlined" sx={{ borderColor: '#d1d5db', color: '#6b7280' }}>Cancel</Button>
          <Button onClick={handleActivate} variant="contained" sx={{ backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}>Activate</Button>
        </DialogActions>
      </Dialog>

      {/* Verify Confirmation */}
      <Dialog open={verifyModalOpen} onClose={handleCloseVerifyModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#f0fdf4', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px' }}>✔️</Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Accept & Verify Stock Transfer</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to accept and verify transfer <strong>{verifyItem?.TransferPrefix && verifyItem?.FinYear ? `${verifyItem.TransferPrefix}/${verifyItem.TransferNumber}/${verifyItem.FinYear}` : verifyItem?.TransferNumber}</strong>?
          </Typography>
          {verifyItem && (
            <Box sx={{ mt: 1.5, bgcolor: '#f8fafc', borderRadius: 1, p: 1.5, border: '1px solid #e2e8f0' }}>
              {verifyItem.InputAccepted != null && (
                <Typography sx={{ fontSize: '0.82rem', color: '#16a34a' }}>Accepted Qty: <strong>{verifyItem.InputAccepted}</strong></Typography>
              )}
              {verifyItem.InputRejected != null && (
                <Typography sx={{ fontSize: '0.82rem', color: verifyItem.InputRejected > 0 ? '#dc2626' : '#64748b' }}>Rejected Qty: <strong>{verifyItem.InputRejected}</strong></Typography>
              )}
            </Box>
          )}
          <Typography sx={{ color: '#f59e0b', fontSize: '0.85rem', mt: 1, fontWeight: 500 }}>This will mark the transfer as verified and cannot be edited after.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseVerifyModal} variant="outlined" sx={{ borderColor: '#d1d5db', color: '#6b7280' }}>Cancel</Button>
          <Button onClick={handleVerify} variant="contained" sx={{ backgroundColor: '#16a34a', '&:hover': { backgroundColor: '#15803d' } }}>Accept & Verify</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars */}
      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert onClose={handleCloseSnackbar} severity="error" sx={{ width: '100%' }}>{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default StockTransferInIndex;

